import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Plus, Trash2, Download, TestTube2, PoundSterling, ShieldAlert } from "lucide-react";
import {
  benchmarkConfig, benchmarkPreflight, benchmarkListPapers, benchmarkAddPaper,
  benchmarkDeletePaper, benchmarkRun, benchmarkListRuns, benchmarkRunDetail,
  benchmarkRunCsvUrl,
} from "../../lib/api";

const STATUS_COLOR = {
  ok: "bg-emerald-100 text-emerald-800",
  unavailable: "bg-amber-100 text-amber-800",
  error: "bg-red-100 text-red-800",
  budget_stopped: "bg-zinc-200 text-zinc-800",
  running: "bg-indigo-100 text-indigo-800",
  complete: "bg-emerald-100 text-emerald-800",
};

export default function BenchmarkPanel() {
  const [config, setConfig] = useState(null);
  const [papers, setPapers] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedModelIds, setSelectedModelIds] = useState([]);
  const [preflight, setPreflight] = useState(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    title: "", ground_truth_grade: "", brief: "", rubric: "", submission_text: "", reference_style: "Harvard",
  });
  const [runDetail, setRunDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    benchmarkConfig().then((c) => {
      setConfig(c);
      setSelectedModelIds(c.candidate_models.map((m) => m.api_id));
    }).catch(() => toast.error("Could not load benchmark config."));
    refreshPapers();
    refreshRuns();
  }, []);

  const refreshPapers = () => benchmarkListPapers().then((d) => setPapers(d.papers || [])).catch(() => {});
  const refreshRuns = () => benchmarkListRuns(20).then((d) => setRuns(d.runs || [])).catch(() => {});

  const toggleModel = (mid) => {
    setSelectedModelIds((ids) => ids.includes(mid) ? ids.filter((x) => x !== mid) : [...ids, mid]);
    setPreflight(null);
  };

  const doPreflight = async () => {
    if (!selectedModelIds.length) return toast.error("Pick at least one candidate model.");
    setPreflightBusy(true);
    try {
      const p = await benchmarkPreflight({
        corpus_size: papers.length,
        candidate_model_ids: selectedModelIds,
      });
      setPreflight(p);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preflight failed"); }
    finally { setPreflightBusy(false); }
  };

  const doRun = async () => {
    if (!papers.length) return toast.error("Add at least one paper to the corpus.");
    if (!selectedModelIds.length) return toast.error("Pick at least one candidate model.");
    if (!dryRun) {
      const cost = preflight?.estimated_total_gbp ?? "?";
      if (!window.confirm(`This will incur real API cost of approximately £${cost}. Continue?`)) return;
    }
    setRunBusy(true);
    try {
      const r = await benchmarkRun({
        candidate_model_ids: selectedModelIds,
        dry_run: dryRun,
      });
      toast.success(`Run ${r.run_id} complete — ${r.cell_count} cells (£${r.actual_cost_gbp} actual)`);
      refreshRuns();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunBusy(false); }
  };

  const doAdd = async () => {
    if (!addForm.title || !addForm.submission_text) return toast.error("Title + submission text required.");
    if (papers.length >= (config?.max_papers ?? 20)) return toast.error(`Max ${config?.max_papers} papers.`);
    try {
      const payload = { ...addForm };
      payload.ground_truth_grade = payload.ground_truth_grade === "" ? null : Number(payload.ground_truth_grade);
      await benchmarkAddPaper(payload);
      toast.success("Paper added");
      setAddOpen(false);
      setAddForm({ title: "", ground_truth_grade: "", brief: "", rubric: "", submission_text: "", reference_style: "Harvard" });
      refreshPapers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const doDelete = async (paperId) => {
    if (!window.confirm("Delete this paper from corpus?")) return;
    try {
      await benchmarkDeletePaper(paperId);
      refreshPapers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openRun = async (runId) => {
    setDetailLoading(true);
    try {
      const d = await benchmarkRunDetail(runId);
      setRunDetail(d);
    } catch (e) { toast.error("Could not load run detail"); }
    finally { setDetailLoading(false); }
  };

  return (
    <div data-testid="admin-benchmark" className="space-y-6">
      {/* Header + hard caps */}
      <div className="border-2 border-amber-300 bg-amber-50 p-4 flex gap-3 items-start" data-testid="benchmark-safety-banner">
        <ShieldAlert className="text-amber-700 shrink-0 mt-0.5" size={18} />
        <div className="text-xs text-amber-900 leading-relaxed">
          <p className="font-semibold">Safety caps enforced by backend</p>
          <p>Max papers: <b>{config?.max_papers ?? "—"}</b> · Max run budget: <b>£{config?.max_run_budget_gbp ?? "—"}</b> · Every run must be triggered here manually — no auto-schedule.</p>
        </div>
      </div>

      {/* Candidate models */}
      <div className="border border-border bg-card p-4" data-testid="benchmark-candidates">
        <p className="overline text-oxford mb-2">Candidate models</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {(config?.candidate_models || []).map((m) => (
            <label key={m.api_id} className={`border ${selectedModelIds.includes(m.api_id) ? "border-oxford bg-oxford/5" : "border-border bg-card"} px-3 py-2 cursor-pointer flex items-start gap-2`}
              data-testid={`benchmark-model-${m.api_id}`}>
              <input type="checkbox" checked={selectedModelIds.includes(m.api_id)} onChange={() => toggleModel(m.api_id)}
                className="mt-0.5 accent-[#1B365D]" data-testid={`benchmark-model-toggle-${m.api_id}`} />
              <div className="flex-1 text-xs">
                <div className="font-semibold text-foreground">{m.display_name}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{m.api_id} · {m.tier}</div>
                <div className="text-[10px] text-muted-foreground mt-1">${m.input_per_m_usd}/$${m.output_per_m_usd} per 1M</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Corpus */}
      <div className="border border-border bg-card p-4" data-testid="benchmark-corpus">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="overline text-oxford">Corpus</p>
            <p className="text-xs text-muted-foreground">{papers.length} / {config?.max_papers ?? 20} papers</p>
          </div>
          <button onClick={() => setAddOpen(true)} className="bg-oxford text-white px-3 py-1.5 text-xs hover:bg-[#2C5282] flex items-center gap-1.5" data-testid="benchmark-add-paper-btn">
            <Plus size={12} /> Add paper
          </button>
        </div>
        <div className="border border-border overflow-x-auto text-sm">
          <table className="w-full">
            <thead className="bg-secondary">
              <tr><th className="text-left px-3 py-2 text-xs font-mono">Title</th>
                <th className="text-left px-3 py-2 text-xs font-mono">Ground-truth grade</th>
                <th className="text-left px-3 py-2 text-xs font-mono">Ref style</th>
                <th className="text-right px-3 py-2 text-xs font-mono">Actions</th></tr>
            </thead>
            <tbody className="text-[13px]">
              {papers.length === 0 && (<tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Corpus is empty. Add a paper to get started.</td></tr>)}
              {papers.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`benchmark-paper-${p.id}`}>
                  <td className="px-3 py-2">{p.title || "—"}</td>
                  <td className="px-3 py-2 font-mono">{p.ground_truth_grade ?? "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{p.reference_style || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => doDelete(p.id)} className="text-red-600 hover:bg-red-50 p-1" data-testid={`benchmark-delete-${p.id}`}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preflight + Run */}
      <div className="border border-border bg-card p-4" data-testid="benchmark-run-controls">
        <p className="overline text-oxford mb-2">Preflight & run</p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={doPreflight} disabled={preflightBusy || !selectedModelIds.length} className="bg-secondary border border-border px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="benchmark-preflight-btn">
            {preflightBusy ? <Loader2 size={12} className="animate-spin" /> : <PoundSterling size={12} />} Estimate cost
          </button>
          <label className="text-xs flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="accent-[#1B365D]" data-testid="benchmark-dry-run-toggle" />
            <span>Dry-run (no live API calls, free)</span>
          </label>
          <button onClick={doRun} disabled={runBusy || !papers.length || !selectedModelIds.length} className={`px-3 py-1.5 text-xs flex items-center gap-1.5 text-white ${dryRun ? "bg-zinc-700 hover:bg-zinc-800" : "bg-red-600 hover:bg-red-700"}`} data-testid="benchmark-run-btn">
            {runBusy ? <Loader2 size={12} className="animate-spin" /> : (dryRun ? <TestTube2 size={12} /> : <Play size={12} />)}
            {dryRun ? "Run dry" : "Run for real (spends money)"}
          </button>
        </div>
        {preflight && (
          <div className="bg-secondary p-3 text-xs font-mono" data-testid="benchmark-preflight-result">
            <p>Per-paper: ${preflight.per_paper_usd} (£{preflight.per_paper_gbp}) across {preflight.candidate_model_count} models</p>
            <p><b>Total estimate for {preflight.corpus_size} papers: ${preflight.estimated_total_usd} (£{preflight.estimated_total_gbp})</b> · budget cap £{preflight.max_budget_gbp}</p>
            {preflight.would_exceed_budget && <p className="text-red-600 font-semibold mt-1">⚠ Exceeds budget cap — backend will refuse this run.</p>}
          </div>
        )}
      </div>

      {/* Historical runs */}
      <div className="border border-border bg-card p-4" data-testid="benchmark-runs">
        <p className="overline text-oxford mb-2">Historical runs</p>
        <div className="border border-border overflow-x-auto text-sm">
          <table className="w-full">
            <thead className="bg-secondary"><tr>
              <th className="text-left px-3 py-2 text-xs font-mono">Run</th>
              <th className="text-left px-3 py-2 text-xs font-mono">When</th>
              <th className="text-left px-3 py-2 text-xs font-mono">Status</th>
              <th className="text-left px-3 py-2 text-xs font-mono">Papers × Models</th>
              <th className="text-left px-3 py-2 text-xs font-mono">Estimated / Actual (£)</th>
              <th className="text-right px-3 py-2 text-xs font-mono">Actions</th></tr>
            </thead>
            <tbody className="text-[13px]">
              {runs.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No runs yet.</td></tr>}
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`benchmark-run-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2 text-xs">{new Date(r.started_at).toLocaleString()}</td>
                  <td className="px-3 py-2"><span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_COLOR[r.status] || "bg-zinc-100 text-zinc-800"}`}>{r.status}</span></td>
                  <td className="px-3 py-2 text-xs">{r.corpus_size} × {r.candidate_models?.length || 0}{r.dry_run && <span className="ml-1 text-amber-600">(dry)</span>}</td>
                  <td className="px-3 py-2 text-xs font-mono">£{r.estimated_cost_gbp} / £{r.actual_cost_gbp}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openRun(r.id)} className="text-oxford underline text-xs mr-2" data-testid={`benchmark-open-run-${r.id}`}>Open</button>
                    <a href={benchmarkRunCsvUrl(r.id)} target="_blank" rel="noreferrer" className="text-oxford underline text-xs" data-testid={`benchmark-csv-${r.id}`}><Download size={11} className="inline" /> CSV</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add paper modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAddOpen(false)} data-testid="benchmark-add-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6">
            <h3 className="font-serif text-2xl mb-3">Add benchmark paper</h3>
            <div className="space-y-3 text-sm">
              <label className="block"><span className="block text-xs text-muted-foreground mb-1">Title *</span><input value={addForm.title} onChange={(e) => setAddForm({ ...addForm, title: e.target.value })} className="w-full border border-border px-2 py-1.5" data-testid="benchmark-add-title" /></label>
              <label className="block"><span className="block text-xs text-muted-foreground mb-1">Ground-truth grade (0-100)</span><input value={addForm.ground_truth_grade} onChange={(e) => setAddForm({ ...addForm, ground_truth_grade: e.target.value })} type="number" min="0" max="100" step="1" className="w-full border border-border px-2 py-1.5 font-mono" data-testid="benchmark-add-grade" /></label>
              <label className="block"><span className="block text-xs text-muted-foreground mb-1">Reference style</span><input value={addForm.reference_style} onChange={(e) => setAddForm({ ...addForm, reference_style: e.target.value })} className="w-full border border-border px-2 py-1.5" /></label>
              <label className="block"><span className="block text-xs text-muted-foreground mb-1">Brief</span><textarea value={addForm.brief} onChange={(e) => setAddForm({ ...addForm, brief: e.target.value })} rows={3} className="w-full border border-border px-2 py-1.5 text-xs" /></label>
              <label className="block"><span className="block text-xs text-muted-foreground mb-1">Rubric</span><textarea value={addForm.rubric} onChange={(e) => setAddForm({ ...addForm, rubric: e.target.value })} rows={3} className="w-full border border-border px-2 py-1.5 text-xs" /></label>
              <label className="block"><span className="block text-xs text-muted-foreground mb-1">Submission text *</span><textarea value={addForm.submission_text} onChange={(e) => setAddForm({ ...addForm, submission_text: e.target.value })} rows={8} className="w-full border border-border px-2 py-1.5 text-xs font-mono" data-testid="benchmark-add-submission" /></label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setAddOpen(false)} className="border border-border px-3 py-1.5 text-xs">Cancel</button>
              <button onClick={doAdd} className="bg-oxford text-white px-3 py-1.5 text-xs hover:bg-[#2C5282]" data-testid="benchmark-add-save">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Run detail modal */}
      {runDetail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRunDetail(null)} data-testid="benchmark-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border max-w-5xl w-full max-h-[85vh] overflow-y-auto p-6">
            {detailLoading ? <Loader2 size={16} className="animate-spin" /> : (
              <>
                <h3 className="font-serif text-2xl mb-1">Run {runDetail.run.id}</h3>
                <p className="text-xs text-muted-foreground mb-3">{runDetail.run.corpus_size} papers × {runDetail.run.candidate_models?.length || 0} models · £{runDetail.run.actual_cost_gbp} actual / £{runDetail.run.estimated_cost_gbp} estimated</p>
                <div className="border border-border overflow-x-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-secondary"><tr>
                      <th className="text-left px-2 py-1.5 font-mono">Paper</th>
                      <th className="text-left px-2 py-1.5 font-mono">Model</th>
                      <th className="text-left px-2 py-1.5 font-mono">Status</th>
                      <th className="text-right px-2 py-1.5 font-mono">Q-score</th>
                      <th className="text-right px-2 py-1.5 font-mono">Conf</th>
                      <th className="text-right px-2 py-1.5 font-mono">Halluc</th>
                      <th className="text-right px-2 py-1.5 font-mono">In/Out tok</th>
                      <th className="text-right px-2 py-1.5 font-mono">$</th>
                      <th className="text-right px-2 py-1.5 font-mono">Latency</th></tr>
                    </thead>
                    <tbody>
                      {(runDetail.cells || []).map((c, i) => (
                        <tr key={i} className="border-t border-border" data-testid={`benchmark-cell-${c.paper_id}-${c.model_api_id}`}>
                          <td className="px-2 py-1 font-mono">{c.paper_id}</td>
                          <td className="px-2 py-1">{c.model_display_name}</td>
                          <td className="px-2 py-1"><span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase ${STATUS_COLOR[c.status] || "bg-zinc-100 text-zinc-800"}`}>{c.status}</span></td>
                          <td className="px-2 py-1 text-right font-mono">{c.academic_quality_score ?? "—"}</td>
                          <td className="px-2 py-1 text-right font-mono">{c.confidence ?? "—"}</td>
                          <td className="px-2 py-1 text-right font-mono">{c.hallucination_flags}</td>
                          <td className="px-2 py-1 text-right font-mono">{c.input_tokens}/{c.output_tokens}</td>
                          <td className="px-2 py-1 text-right font-mono">${c.cost_usd}</td>
                          <td className="px-2 py-1 text-right font-mono">{c.latency_ms}ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex justify-end">
                  <button onClick={() => setRunDetail(null)} className="border border-border px-3 py-1.5 text-xs">Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
